<template>
	<SSOConfigurationLayout
		title="SSO Configuration"
		caption="Manage SSO Configuration for the Cassie Admin Portal & Customer Service Portal"
	>
		<template #content>
			<v-container class="fill-height">
				<v-row justify="center">
					<v-col cols="10">
						<div class="cards-grid">
							<div
								v-for="item in filteredSSOConfiguration.filter(Boolean)"
								:key="item.productId || item.id"
								class="d-flex"
							>
								<OverviewCard
									:title="getName(item)"
									:icon="getIcon(item.productId)"
									:description="getDescription(item)"
									:user-can-create-and-update="userFullPermissions"
									@overview="configureSSO(item)"
								>
									<template
										v-if="item.scimEnabled"
										#top-right
									>
										<v-menu
											offset-y
											left
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item @click="openScimLogs(item)">
													<v-list-item-content>
														<v-list-item-title>
															View SCIM Logs
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</template>
									<template
										v-if="item.scimEnabled || isInProgress(item)"
										#title-right
									>
										<div
											class="d-flex align-center"
											style="gap: 8px;"
										>
											<Chip
												v-if="item.scimEnabled"
												color="primary"
											>
												SCIM
											</Chip>
											<Chip
												v-if="isInProgress(item)"
												color="prussian-blue"
												text-color="white"
											>
												In Progress
											</Chip>
										</div>
									</template>
									<template #extra>
										<div
											v-if="notConfigured(item)"
											class="d-flex align-center justify-center"
										>
											<SecondaryActionButton
												@click="configureSSO(item)"
											>
												Get Started
											</SecondaryActionButton>
										</div>
										<div
											v-else-if="canBeEnabled(item)"
											class="d-flex align-center"
										>
											<Toggle
												v-if="showConfigureIcon(item.productId) && userFullPermissions"
												:key="`toggle-${item.id}-${toggleResetKey}`"
												:value="item.isEnabled"
												show-status-label
												@update:value="val => setSSOEnabled(item, val)"
											/>
										</div>
									</template>
								</OverviewCard>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>

			<!-- Disable SSO with SCIM Confirmation Modal -->
			<Modal
				v-if="showDisableScimModal"
				max-width="500"
			>
				<template #modal-title>
					Disable SSO Configuration
				</template>
				<template #modal-content>
					<p class="mb-4">
						Disabling this SSO configuration will also disable the associated SCIM provisioning.
					</p>
					<p>
						If you re-enable SSO in the future, you also need to re-enable SCIM within the SSO Configuration.
					</p>
				</template>
				<template #modal-footer>
					<SecondaryActionButton @click="cancelDisableScim">
						Cancel
					</SecondaryActionButton>
					<PrimaryActionButton @click="confirmDisableScim">
						Disable SSO
					</PrimaryActionButton>
				</template>
			</Modal>

			<!-- SCIM Logs Sheet -->
			<ScimLogsSheet
				:is-open="showScimLogsSheet"
				:configuration-name="scimLogsConfigurationName"
				:configuration-id="scimLogsConfigurationId"
				@close="closeScimLogs"
			/>
		</template>
	</SSOConfigurationLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import SSOConfigurationLayout from './sso-configuration-layout.vue'
import OverviewCard from '../../../../../../shared/components/overview-card.vue'
import Toggle from '../../../../../../shared/components/toggle.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import Chip from '../../../../../../shared/components/chip.vue'
import Modal from '../../../../../../shared/components/modal.vue'
import ScimLogsSheet from './scim-logs-sheet.vue'
import { getSSOConfiguration, toggleSSOConfiguration } from '../../../../../../shared/utils/api/sso-configuration.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { SSO_CONFIGURATION_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { MANAGE_SSO_CONFIGURATION } from '../../../../router/route-names.js'
export default {
	components: {
		SSOConfigurationLayout,
		OverviewCard,
		Toggle,
		SecondaryActionButton,
		PrimaryActionButton,
		Chip,
		Modal,
		ScimLogsSheet
	},
	data () {
		const ADMIN_PORTAL = 1
		const CS_PORTAL = 2
		return {
			ssoConfigurations: [],
			lookupData: {},
			ADMIN_PORTAL,
			CS_PORTAL,
			showDisableScimModal: false,
			itemToDisable: null,
			toggleResetKey: 0,
			showScimLogsSheet: false,
			scimLogsConfigurationName: '',
			scimLogsConfigurationId: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userFullPermissions () {
			return this.productAreaPermission(SSO_CONFIGURATION_FULL_PERMISSIONS)
		},
		filteredSSOConfiguration () {
			const adminPortalSSO = this.ssoConfigurations.filter(sso => sso.productId === this.ADMIN_PORTAL)
			const csPortalSSO = this.ssoConfigurations.filter(sso => sso.productId === this.CS_PORTAL)

			const activeAdminPortalSSO = adminPortalSSO.filter(sso => sso.isEnabled)
			const activeCsPortalSSO = csPortalSSO.filter(sso => sso.isEnabled)

			const ssoConfigurations = []
			// Admin Portal selection
			if (activeAdminPortalSSO.length === 1) {
				ssoConfigurations.push(activeAdminPortalSSO[0])
			} else if (adminPortalSSO.length >= 2) {
				const mostRecent = adminPortalSSO.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
				ssoConfigurations.push(mostRecent[0])
			} else if (adminPortalSSO.length === 1) {
				ssoConfigurations.push(adminPortalSSO[0])
			}

			// CS Portal selection
			if (activeCsPortalSSO.length === 1) {
				ssoConfigurations.push(activeCsPortalSSO[0])
			} else if (csPortalSSO.length >= 2) {
				const mostRecent = csPortalSSO.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
				ssoConfigurations.push(mostRecent[0])
			} else if (csPortalSSO.length === 1) {
				ssoConfigurations.push(csPortalSSO[0])
			}

			return ssoConfigurations
		}
	},
	async created () {
		await this.getSSOConfiguration()
	},
	methods: {
		async getSSOConfiguration () {
			const { data: { ssoConfigurations, lookupData } } = await getSSOConfiguration()
			this.ssoConfigurations = ssoConfigurations
			this.lookupData = lookupData
		},
		notConfigured (item) {
			return item.createdDate === 'Not Configured' || item.id === 0
		},
		canBeEnabled (item) {
			const has = v => typeof v === 'string' ? v.trim().length > 0 : v != null && v !== false
			return item.id > 0 &&
				has(item.authority) &&
				has(item.remoteLogoutEndpointUrl) &&
				has(item.identityClaimTypeName) &&
				has(item.clientID)
		},
		isInProgress (item) {
			return !this.notConfigured(item) && !this.canBeEnabled(item)
		},
		getName (item) {
			if (item.name) {
				return `${item.name} - ${item.productName}`
			}
			// Fallback to product name if no custom name is provided
			return item.productName
		},
		getIcon (productId) {
			if (productId === this.ADMIN_PORTAL) return 'mdi-account-circle'
			if (productId === this.CS_PORTAL) return 'mdi-headset'
			return 'mdi-lock-outline'
		},
		getDescription (item) {
			// If the item is not configured, return product-specific guidance text
			if (this.notConfigured(item)) {
				if (item.productId === this.ADMIN_PORTAL) {
					return 'Set up Single Sign-On to simplify secure access to the Administration Portal.'
				}
				if (item.productId === this.CS_PORTAL) {
					return 'Set up Single Sign-On to simplify secure access to the Customer Service Portal.'
				}
				return 'Single Sign-On is not yet configured. Click Configure to begin.'
			}
			// If configuration exists but required data is incomplete (cannot be enabled yet)
			if (!this.canBeEnabled(item)) {
				return 'Single Sign-On setup is incomplete. Continue configuration to enable secure access.'
			}
			// Fully configured (all required details supplied) – single description regardless of enabled/disabled state
			return 'Single Sign-On is configured. You can enable or disable it from this card.'
		},
		getSSOTestId (productName) {
			return productName.toLowerCase().replace(/ /g, '-')
		},
		showConfigureIcon (productId) {
			if (productId === this.ADMIN_PORTAL) {
				return true
			}	else if (productId === this.CS_PORTAL && this.lookupData?.csPortalIsAllowed) {
				return true
			} else {
				return false
			}
		},
		configureSSO (item) {
			this.$router.push({
				name: MANAGE_SSO_CONFIGURATION,
				params: {
					ssoConfigurationToManage: item,
					lookupData: this.lookupData
				}
			})
		},
		async setSSOEnabled (item, enabled) {
			// If disabling and SCIM is enabled, show confirmation modal
			if (!enabled && item.scimEnabled) {
				this.itemToDisable = item
				this.showDisableScimModal = true
				return
			}

			await this.performToggleSSO(item, enabled)
		},
		async performToggleSSO (item, enabled) {
			try {
				await toggleSSOConfiguration({
					enabled,
					productId: item.productId,
					systemConfigurationId: item.id
				})
				item.isEnabled = enabled
				// When disabling SSO, SCIM is also disabled by the backend
				if (!enabled && item.scimEnabled) {
					item.scimEnabled = false
				}
				showSnackbar(`SSO ${enabled ? 'enabled' : 'disabled'} for ${item.productName}`)
			} catch (err) {
				showSnackbar(`Failed to ${enabled ? 'enable' : 'disable'} SSO`, 'error')
			}
		},
		cancelDisableScim () {
			// Increment key to force Toggle re-render with correct state
			this.toggleResetKey++
			this.showDisableScimModal = false
			this.itemToDisable = null
		},
		async confirmDisableScim () {
			if (this.itemToDisable) {
				await this.performToggleSSO(this.itemToDisable, false)
			}
			this.showDisableScimModal = false
			this.itemToDisable = null
		},
		openScimLogs (item) {
			this.scimLogsConfigurationName = this.getName(item)
			this.scimLogsConfigurationId = item.id
			this.showScimLogsSheet = true
		},
		closeScimLogs () {
			this.showScimLogsSheet = false
			this.scimLogsConfigurationName = ''
			this.scimLogsConfigurationId = null
		}
	}
}
</script>

<style lang="scss" scoped>
.cards-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 24px;
	padding: 0;

	@media (min-width: 1264px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 959px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
}
</style>
